import { useEffect, useSyncExternalStore } from "react";
import { useApi } from "@/hooks/useApi";
import { useLatestRef } from "@/hooks/useLatestRef";

export type ImageSize = { width: number; height: number };

export type SessionInlineImage = {
  /** Set once the image is registry-bound; the final message embeds it too. */
  url: string | null;
  size: ImageSize | null;
};

const PLAIN_IMAGE_FILENAME_RE = /^[\w.-]+\.(?:png|jpe?g|gif|webp|avif|bmp)$/i;

/**
 * The session image a markdown `src` names, mirroring the backend's
 * SessionImageIndex::resolvableFilename, e.g. `/root/.x/shot.png` → `shot.png`,
 * `cdn.example.com/a.png` → null.
 */
export function sessionImageFilename(src: string): string | null {
  if (/^(?:[a-z][a-z\d+.-]*:|\/\/|#)/i.test(src)) return null;
  const segments = src.replace(/\\/g, "/").split("/");
  const first = segments[0];
  if (segments.length > 1 && first.includes(".") && !first.startsWith(".")) {
    return null;
  }
  const basename = segments[segments.length - 1];
  return PLAIN_IMAGE_FILENAME_RE.test(basename) ? basename : null;
}

// Entries are a few bytes each, so neither map is bounded. sizesByImage lets
// the final message's capability-URL image keep the box its streamed
// placeholder reserved, and holds the sizes a session payload carries
// (rememberImageSizes).
type Lookup = {
  image: SessionInlineImage | null;
  attempts: number;
  /** URL a reused filename showed before it was invalidated; see invalidate. */
  supersededUrl?: string;
};
const lookupsByKey = new Map<string, Lookup>();
const sizesByImage = new Map<string, ImageSize>();
const inflightKeys = new Set<string>();
const listeners = new Set<() => void>();

// A streamed ref can reach the browser before its file part is persisted, so
// a miss (404, or no URL yet) is retried while the ref stays mounted.
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000];

// A later turn can reuse a filename (`shot.png`) for a new screenshot, and
// the backend resolves the newest one. So the first ref to mount after a
// filename left view re-resolves it, showing the placeholder at the size the
// old image had rather than the old image. A ref that remounts within the
// grace window (markdown re-tree mid-stream) keeps its resolved image.
const REMOUNT_GRACE_MS = 1000;
const mountedRefsByKey = new Map<string, number>();
const lastUnmountAtByKey = new Map<string, number>();

const ASSET_REF_RE =
  /\/api\/attachments\/([\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}\.[\da-f]{16})$/;

// A session asset's URL ends in its ref (AttachmentDeliveryService::assetRef),
// the key session payloads send sizes under; any other URL is its own key.
function imageKey(url: string): string {
  return ASSET_REF_RE.exec(url)?.[1] ?? url;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function publish(key: string, image: SessionInlineImage | null) {
  const previous = lookupsByKey.get(key);
  lookupsByKey.set(key, {
    image,
    attempts: (previous?.attempts ?? 0) + 1,
    supersededUrl: previous?.supersededUrl,
  });
  if (image?.url && image.size)
    sizesByImage.set(imageKey(image.url), image.size);
  listeners.forEach((listener) => listener());
}

function reusedAfterLeavingView(key: string): boolean {
  return (
    Boolean(lookupsByKey.get(key)?.image?.url) &&
    !mountedRefsByKey.get(key) &&
    Date.now() - (lastUnmountAtByKey.get(key) ?? 0) > REMOUNT_GRACE_MS
  );
}

// Until the new file part is persisted the backend still resolves the old
// one, so a refetch that returns the superseded URL counts as a miss while
// retries remain. The last attempt accepts it: the turn may embed the same
// image again.
function invalidate(key: string) {
  const previous = lookupsByKey.get(key)?.image;
  lookupsByKey.set(key, {
    image: { url: null, size: previous?.size ?? null },
    attempts: 0,
    supersededUrl: previous?.url ?? undefined,
  });
  listeners.forEach((listener) => listener());
}

type InlineImageApi = {
  get: (
    url: string,
    config: { params: { filename: string } },
  ) => Promise<{
    data?: { url?: unknown; width?: unknown; height?: unknown };
  }>;
};

function positiveInt(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value > 0
    ? value
    : null;
}

function fetchInlineImage(
  api: InlineImageApi,
  sessionId: string,
  filename: string,
) {
  const key = `${sessionId}:${filename}`;
  if (lookupsByKey.get(key)?.image?.url || inflightKeys.has(key)) return;
  inflightKeys.add(key);
  api
    .get(`/assistant/sessions/${sessionId}/inline-image`, {
      params: { filename },
    })
    .then(({ data }) => {
      const lookup = lookupsByKey.get(key);
      const url = typeof data?.url === "string" ? data.url : null;
      const width = positiveInt(data?.width);
      const height = positiveInt(data?.height);
      const retriesLeft = (lookup?.attempts ?? 0) < RETRY_DELAYS_MS.length;
      const stillOld = url !== null && url === lookup?.supersededUrl;
      publish(key, {
        url: stillOld && retriesLeft ? null : url,
        size: width && height ? { width, height } : null,
      });
    })
    // A failed retry keeps the size an earlier attempt measured.
    .catch(() => publish(key, lookupsByKey.get(key)?.image ?? null))
    .finally(() => inflightKeys.delete(key));
}

/**
 * `undefined` while the lookup is in flight; `null` without a session or when
 * the session has no readable image by that name.
 */
export function useSessionInlineImage(
  sessionId: string | undefined,
  filename: string,
): SessionInlineImage | null | undefined {
  const api = useApi();
  const apiRef = useLatestRef(api);
  const key = sessionId ? `${sessionId}:${filename}` : undefined;
  const lookup = useSyncExternalStore(
    subscribe,
    () => (key ? lookupsByKey.get(key) : undefined),
    () => undefined,
  );

  useEffect(() => {
    if (!sessionId) return;
    const mountKey = `${sessionId}:${filename}`;
    const revalidate = reusedAfterLeavingView(mountKey);
    mountedRefsByKey.set(mountKey, (mountedRefsByKey.get(mountKey) ?? 0) + 1);
    if (revalidate) {
      invalidate(mountKey);
      fetchInlineImage(apiRef.current, sessionId, filename);
    }
    return () => {
      const remaining = (mountedRefsByKey.get(mountKey) ?? 1) - 1;
      mountedRefsByKey.set(mountKey, remaining);
      if (remaining === 0) lastUnmountAtByKey.set(mountKey, Date.now());
    };
  }, [apiRef, sessionId, filename]);

  useEffect(() => {
    if (!sessionId) return;
    const fetchNow = () =>
      fetchInlineImage(apiRef.current, sessionId, filename);
    if (!lookup) {
      fetchNow();
      return;
    }
    if (lookup.image?.url) return;
    const delay = RETRY_DELAYS_MS[lookup.attempts - 1];
    if (delay === undefined) return;
    const timer = setTimeout(fetchNow, delay);
    return () => clearTimeout(timer);
  }, [apiRef, sessionId, filename, lookup]);

  if (!key) return null;
  if (!lookup) return undefined;
  // The render before the mount effect revalidates must not flash the old image.
  if (reusedAfterLeavingView(key)) {
    return { url: null, size: lookup.image?.size ?? null };
  }
  return lookup.image;
}

export function useKnownImageSize(url: string): ImageSize | undefined {
  return useSyncExternalStore(
    subscribe,
    () => sizesByImage.get(imageKey(url)),
    () => undefined,
  );
}

/** Sizes the backend recorded when it wrote a session's images, keyed by asset ref. */
export function rememberImageSizes(
  sizes: Record<string, { width?: unknown; height?: unknown }> | undefined,
) {
  if (!sizes) return;
  let changed = false;
  for (const [ref, size] of Object.entries(sizes)) {
    const width = positiveInt(size?.width);
    const height = positiveInt(size?.height);
    if (!width || !height || sizesByImage.has(ref)) continue;
    sizesByImage.set(ref, { width, height });
    changed = true;
  }
  if (changed) listeners.forEach((listener) => listener());
}
